import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { MdOutlineClear } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'

import type { ReactElement } from 'react'
import type { IUserAdmin } from '~/types/user'

import AddGroupsModal from './AddGroupsModal'
import AddRolesModal from './AddRolesModal'

import Section, { SectionItem } from '../../../shared/Section'

import LayoutGroups from '~/components/Layouts/Groups'
import ModalDialog from '~/components/ModalDialog'
import {
  useDeleteGroupsFromUserMutation,
  useDeleteRolesFromUserMutation,
  keys as usersManagementKeys,
} from '~/queries/user-management'
import { displayToast } from '~/utils/toast'

interface Props {
  user: IUserAdmin
}

const UserDetailsGroups = ({ user }: Props): ReactElement => {
  const { distinct_authorizations: authorizations = [], groups = [], roles = [], user_id: userId } = user

  const queryClient = useQueryClient()

  const navigate = useNavigate()

  const { mutateAsync: deleteGroupMutation } = useDeleteGroupsFromUserMutation()

  const { mutateAsync: deleteRolesMutation } = useDeleteRolesFromUserMutation()

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)

  const [isGroupsModalOpen, setGroupsModalOpen] = useState(false)

  const [isRolesModalOpen, setRolesModalOpen] = useState(false)

  const [deleteModalType, setDeleteModalType] = useState<'group' | 'role' | undefined>()

  const [deletedItemId, setDeletedItemId] = useState<string | undefined>()

  const handleOpenGroupsModal = (value: boolean) => {
    return () => setGroupsModalOpen(value)
  }

  const handleAddGroupsFailure = () => {
    setGroupsModalOpen(false)
    displayToast('Groups could not be added', 'error')
  }

  const handleAddGroupsSuccess = () => {
    setGroupsModalOpen(false)
    displayToast('Groups successfully added', 'success')
  }

  const handleOpenRolesModal = (value: boolean) => {
    return () => setRolesModalOpen(value)
  }

  const handleAddRolesFailure = () => {
    displayToast('Roles could not be added', 'error')
    setRolesModalOpen(false)
  }

  const handleAddRolesSuccess = () => {
    setRolesModalOpen(false)
    displayToast('Roles successfully added', 'success')
  }

  const handleOpenDeleteModal = (value: boolean, options?: { id: string; type: 'group' | 'role' }) => {
    return () => {
      setDeleteModalType(options?.type)
      setDeletedItemId(options?.id)
      setDeleteModalOpen(value)
    }
  }

  const handleDeleteGroup = async (groupId: string) => {
    try {
      await deleteGroupMutation({ groupIds: [groupId], userId })
      queryClient.invalidateQueries({ queryKey: usersManagementKeys.details(userId) })
      displayToast('Group successfully removed', 'success')
    } catch {
      displayToast('Group could not be removed', 'error')
    }
  }

  const handleDeleteRole = async (roleId: string) => {
    try {
      await deleteRolesMutation({ roleIds: [roleId], userId })
      queryClient.invalidateQueries({ queryKey: usersManagementKeys.details(userId) })
      displayToast('Role successfully removed', 'success')
    } catch {
      displayToast('Role could not be removed', 'error')
    }
  }

  const handleDelete = async () => {
    if (deletedItemId) {
      try {
        switch (deleteModalType) {
          case 'group':
            await handleDeleteGroup(deletedItemId)
            break
          case 'role':
            await handleDeleteRole(deletedItemId)
            break
          default:
            throw new Error('No type found')
        }
      } catch (e) {
        captureException(e)
        displayToast('Could not remove the group/role', 'error')
      }
    }

    setDeleteModalType(undefined)
    setDeletedItemId(undefined)
    setDeleteModalOpen(false)
  }

  const deleteModalPropsMap = {
    default: {
      mainText: '',
      onConfirm: () => {
        return
      },
      subText: '',
    },
    group: {
      mainText: 'Remove group',
      onConfirm: handleDelete,
      subText: 'Are you sure you want to remove the group from the user?',
    },
    role: {
      mainText: 'Remove role',
      onConfirm: handleDelete,
      subText: 'Are you sure you want to remove the role from the user?',
    },
  }

  const deleteModalProps = deleteModalType ? deleteModalPropsMap[deleteModalType] : deleteModalPropsMap['default']

  const userGroups = groups.map(({ id }) => id)
  const userRoles = roles.map(({ role_id }) => role_id)

  const handleSectionItemClick = (id: string, section: string) => {
    return () => navigate(`/mwm-users/${section}/${id}`)
  }

  return (
    <>
      <LayoutGroups>
        <Section itemsCount={groups.length} onClickAdd={handleOpenGroupsModal(true)} title="Groups">
          {groups.map(({ description, group_name, id }) => (
            <SectionItem
              isNavigable
              key={id}
              onClick={handleSectionItemClick(id, 'groups')}
              onDelete={handleOpenDeleteModal(true, { id, type: 'group' })}
            >
              <div className="item-body">
                <div className="item-title">{group_name}</div>
                <div className="item-subtitle">{description}</div>
              </div>
            </SectionItem>
          ))}
        </Section>
        <Section itemsCount={roles.length} onClickAdd={handleOpenRolesModal(true)} title="Specific roles">
          {roles.map(({ description, name, role_id }) => (
            <SectionItem
              isNavigable
              key={role_id}
              onClick={handleSectionItemClick(role_id, 'roles')}
              onDelete={handleOpenDeleteModal(true, { id: role_id, type: 'role' })}
            >
              <div className="item-body">
                <div className="item-title">{name}</div>
                <div className="item-subtitle">{description}</div>
              </div>
            </SectionItem>
          ))}
        </Section>
        <Section itemsCount={authorizations.length} title="Authorization">
          {authorizations.map((authorization) => (
            <SectionItem key={authorization}>
              <div className="item-body">
                <div className="item-title">{authorization}</div>
              </div>
            </SectionItem>
          ))}
        </Section>
      </LayoutGroups>
      <AddGroupsModal
        isOpen={isGroupsModalOpen}
        onClose={handleOpenGroupsModal(false)}
        onFailure={handleAddGroupsFailure}
        onSuccess={handleAddGroupsSuccess}
        userGroups={userGroups}
        userId={userId}
      />
      <AddRolesModal
        isOpen={isRolesModalOpen}
        onClose={handleOpenRolesModal(false)}
        onFailure={handleAddRolesFailure}
        onSuccess={handleAddRolesSuccess}
        userId={userId}
        userRoles={userRoles}
      />
      <ModalDialog
        {...deleteModalProps}
        icon={<MdOutlineClear size={42} />}
        isOpen={isDeleteModalOpen}
        onClose={handleOpenDeleteModal(false)}
      />
    </>
  )
}

export default UserDetailsGroups
